import { createContext } from 'react';

import type { ApiErrorKey } from '@/services/api/errors';
import type { PortalClient } from '@/services/api/portal-client';
import type { Credentials, PortalUser } from '@/services/api/types';

/**
 * `initializing` covers the startup window in which a stored session is being
 * re-verified against the Portal. Route guards must render a loading state for
 * it instead of redirecting, otherwise a refresh would kick the user to /login.
 */
export type AuthStatus = 'initializing' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  status: AuthStatus;
  user: PortalUser | null;
  /**
   * Translatable key of the last auth failure, or null. The provider never holds
   * display text so the UI stays fully localisable.
   */
  error: ApiErrorKey | null;
  /** True while a login/logout request is in flight. */
  busy: boolean;
  /**
   * True once the signed-in user has an organization — derived from
   * `user.orgID`, or from a local "just created it this session" flag set by
   * {@link markOrganizationCreated} right after organization creation succeeds.
   */
  hasOrganization: boolean;
  /** The underlying Portal client, or null when a test double is injected. */
  client: PortalClient | null;
  login: (credentials: Credentials) => Promise<boolean>;
  /**
   * Establishes an authenticated session directly from a JWT bearer token (used
   * by the verify-email page after `SuiteAPI/User/ConfirmEmailV1`).
   */
  signInWithJwt: (token: string, user: PortalUser) => void;
  /** Replaces the in-memory user after a profile update without changing credentials. */
  updateUser: (user: PortalUser) => void;
  logout: () => Promise<void>;
  clearError: () => void;
  /**
   * Marks the current session as having an organization without waiting for
   * a full session reload. Call right after organization creation succeeds.
   */
  markOrganizationCreated: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
